import type React from "react";

import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import type { HealthReport } from "api/models/HealthReport";

import type { PainLocation, Question } from "../../helpers";
import { getChildAnswer } from "../../helpers";
import { renderAnswer } from "../utils/renderAnswer";

import { AnswerCellContainer, ChangeIndicator } from "./styles";

interface AnswerCellProps {
  report: HealthReport;
  question: Question;
  parent?: Question;
  hasChanged?: boolean;
  painLocation?: PainLocation;
  rowIndex?: number;
}

export const AnswerCell: React.FC<AnswerCellProps> = ({
  report,
  question,
  parent,
  hasChanged = false,
  painLocation,
  rowIndex,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getAnswer = () => {
    if (parent) {
      const parentAnswer = report.answers[parent.key];
      return getChildAnswer(parentAnswer, question.key);
    }
    return report.answers[question.key];
  };

  const answer = getAnswer();
  const isNotAsked = answer === undefined;

  const formattedAnswer = isNotAsked
    ? t("patients.health_reports.questions_content.not_asked")
    : renderAnswer(
        question.type,
        question.key,
        answer,
        question.params || {},
        report.week_number,
        painLocation as PainLocation,
        t
      );

  const showChange = hasChanged && !["header", "hidden_header"].includes(question.type);

  return (
    <AnswerCellContainer
      hasChanged={showChange}
      rowIndex={rowIndex}
      data-testid={`answer-cell-${question.key}-${report.id}`}
    >
      {showChange && <ChangeIndicator data-testid="change-indicator" />}
      <Typography
        variant="body_thin"
        sx={{
          color: isNotAsked ? theme.colors.redesign.db40 : theme.colors.redesign.db100,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {formattedAnswer}
      </Typography>
    </AnswerCellContainer>
  );
};
